import { isNil } from 'lodash-es'
import {
  AssetType,
  CollateralIdentifierType,
  ComplianceStatus,
  CounterpartyInstruction,
  DateFormat,
  Market,
  Order,
  OrderDetail,
  OrderTerm,
  TradeConditionFlag,
  TradingEntity,
  UserPreference
} from 'api'
import { Locale, Translation } from 'locale'
import { OrderTermHelper, Severity, ValidationType } from 'model'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'

export type PositionsGrouping = Translation.SC_GROUP_BY_ASSETS | Translation.SC_GROUP_BY_COUNTERPARTIES

export const corporateCollateralMultipleCounterpartyErrorMessage = {
  errorMessage: Locale.translate(Translation.SC_MBS_COLLATERAL_ERROR_MESSAGE),
  fieldName: 'counterparties',
  severity: Severity.ERROR,
  type: ValidationType.INVALID_VALUE
}

export const gfidMissingErrorMessage = {
  errorMessage: Locale.translate(Translation.SC_MISSING_GFID_ERROR_MESSAGE),
  fieldName: 'counterparties',
  severity: Severity.ERROR,
  type: ValidationType.INVALID_VALUE
}

export function getAssetDescription(orderDetail: OrderDetail, userPreference: UserPreference): string {
  const { orderFund, collateral, collateralBasket, order } = orderDetail

  if (collateralBasket) {
    return collateralBasket.name
  } else {
    const fundName = orderFund ? orderFund.shortName : ''
    const collateralName = collateral ? collateral.name : ''
    const notional = !isNil(order.notional)
      ? `${Locale.formatNumber(order.notional, getNotionalFormatOptions(userPreference.amountFormat, true))}`
      : ''
    const term = getTerm(order, userPreference.dateFormat)
    return `${fundName} ${collateralName} ${notional} ${term}`
  }
}

export function getIsByAssets(chosenGrouping: PositionsGrouping) {
  return chosenGrouping === Translation.SC_GROUP_BY_ASSETS
}

export function areAllAxed(counterpartyInstructions: CounterpartyInstruction[]) {
  if (counterpartyInstructions.length > 0) {
    return counterpartyInstructions.every(instruction => instruction.isAxed)
  }
  return false
}

export function getCommonTradeConditionFlag(counterpartyInstructions: CounterpartyInstruction[]) {
  const conditionFlags = counterpartyInstructions.map(instruction => instruction.tradeConditionFlag)
  const isUnwind = conditionFlags.every(flag => flag === TradeConditionFlag.UNWIND)

  if (isUnwind) {
    return TradeConditionFlag.UNWIND
  }

  const isRoll = conditionFlags.every(flag => flag === TradeConditionFlag.ROLL)

  if (isRoll) {
    return TradeConditionFlag.ROLL
  }

  const isSub = conditionFlags.every(flag => flag === TradeConditionFlag.SUB)

  if (isSub) {
    return TradeConditionFlag.SUB
  }

  return TradeConditionFlag.NONE
}

function getTerm(order: Order, dateFormat: DateFormat) {
  if (!order.term) {
    return ''
  }
  if (order.term === OrderTerm.CUSTOM) {
    const startDate = order.startDate ? Locale.formatDate(order.startDate, dateFormat) : ''
    const endDate = order.endDate ? Locale.formatDate(order.endDate, dateFormat) : ''
    return `${startDate} - ${endDate}`
  }
  return OrderTermHelper.getName(order.term)
}

export function hasCorporateCollateralValidationsErrors(
  ordersToPrepare: PreparedStagingOrder[],
  counterpartyInstructions: CounterpartyInstruction[]
) {
  const collaterals = ordersToPrepare.map(order => order.orderDetail.collateral)
  const hasCorpOrMortCollateral = collaterals.some(
    collateral => collateral?.assetType === AssetType.CORP || collateral?.assetType === AssetType.MORT_NON_AGENCY
  )
  const hasUsCollateral = collaterals.some(collateral => collateral?.collateralIdentifierType === CollateralIdentifierType.CUSIP)
  const moreThanOneCounterpartySelected = counterpartyInstructions.length > 1
  const someOrdersAreNotCleared = ordersToPrepare.some(order => order.orderDetail.order.complianceStatus !== ComplianceStatus.CLEARED)

  return hasCorpOrMortCollateral && hasUsCollateral && moreThanOneCounterpartySelected && someOrdersAreNotCleared
}

export function hasGfidValidationErrors(ordersToPrepare: PreparedStagingOrder[], counterparties: TradingEntity[] | null) {
  if (!counterparties) {
    return false
  }

  const hasEuCollateral = ordersToPrepare.some(order => order.orderDetail.product?.market === Market.EUROPE)
  const hasUsCollateral = ordersToPrepare.some(order => order.orderDetail.product?.market === Market.US)
  const tradingEntityHasEuGfid = counterparties.every(counterparty => counterparty.euGfidCode)
  const tradingEntityHasUsGfid = counterparties.every(counterparty => counterparty.usGfidCode)

  return (hasEuCollateral && !tradingEntityHasEuGfid) || (hasUsCollateral && !tradingEntityHasUsGfid)
}
